import React from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import classes from './LogsManagement.module.scss';
import ManagementLoader from '../Sceletons/ManagementLoader';
import { Button, DatePicker, Dropdown, MenuProps } from 'antd';
import type { Dayjs } from 'dayjs';
import { ILogs, IPreparedLogs } from 'interfaces';
import { setAllLogs, setIsForceUpdating, setLogs } from 'store/slices/dataLogsSlice';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { DownloadOutlined, EllipsisOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import api from 'api';
import { getFile } from 'helpers/downloadSomeFile';
import { getlogsColumnsForPrint } from 'components/LogsTable/columns/logsColumns';
import { pdfHelper } from 'helpers/pdfHelper';
import prepareLogs from 'helpers/prepareLogs';

const { RangePicker } = DatePicker;

export default function LogsManagement() {
  const [downloadProcess, setDownloadProcess] = React.useState<string[]>([]);
  const [isOpenDropdown, setIsOpenDropdown] = React.useState(false);
  const [choosenDates, setChoosenDates] = React.useState<null | (Dayjs | null)[]>(null);
  const [stringDates, setStringDates] = React.useState<string[]>(['','']);

  const { isLoadingRoom, dataRoom } = useAppSelector((store) => store.dataRoom);
  const { allLogs, logs, logsForPrint } = useAppSelector((store) => store.logs);
  const { userAndRoomStatus } = useAppSelector((store) => store.userData);
  const { messageApi } = useAppSelector((store) => store.windowState);

  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en'

  const dispatch = useAppDispatch();

  const rangePresets: {
    label: string;
    value: [Dayjs, Dayjs];
  }[] = [
    { label: t('Logs.presets.7'), value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: t('Logs.presets.14'), value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: t('Logs.presets.30'), value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: t('Logs.presets.90'), value: [dayjs().add(-90, 'd'), dayjs()] },
  ];
  
  React.useEffect(() => {
    if (stringDates[0]) {
      const startPeriod = dayjs(stringDates[0])?.toDate()?.getTime()!;
      const endPeriod = dayjs(stringDates[1])?.toDate()?.getTime()!;

      const filteredLogs = allLogs.filter((log: IPreparedLogs) => {    
        const inDateFormat = log?.date?.split('.')?.reverse()?.join('-');
        const dateInMilliseconds = new Date(new Date(`${inDateFormat}T${log?.time}`))?.getTime();
        return dateInMilliseconds >= startPeriod! && dateInMilliseconds <= endPeriod!;
      });
      dispatch(setLogs(filteredLogs));
    } else {      
      dispatch(setLogs(allLogs));
    }
  },[stringDates, allLogs]);

  const refreshLogs = async () => {
    dispatch(setIsForceUpdating(true));
    try {
      const logs = await api.getLogs(dataRoom!.id, locale);
      dispatch(setAllLogs(prepareLogs(logs.data)));      
    } catch (e) {
    } finally {
      dispatch(setIsForceUpdating(false));
    }
  };

  const onRangeChange = (dates: null | (Dayjs | null)[], dateStrings: string[]) => {    
    setChoosenDates(dates);
    setStringDates(dateStrings);
  };

  const downloadReport = async () => {
    setDownloadProcess(prev => [...prev, 'download']);
    try {
      const localization = locale === 'es' ? 'en' : locale;
      const res = await api.getLogsReport({room_id: dataRoom?.id!, locale: localization});
      getFile(res.data, `${t('Logs.managament.report')}_${dataRoom?.name}.xlsx`);
    } catch (e) {
      messageApi?.error(t('Logs.managament.reportError'));
    } finally {
      setDownloadProcess(downloadProcess.filter(el => el === 'download'));
    }
  };

  const printReport = async () => {
    setDownloadProcess(prev => [...prev, 'print']);
    api.logPrint(dataRoom?.id!, false);
    try {
      const logsColumns = getlogsColumnsForPrint(t);   
      const arrOfLogs = logsForPrint && logsForPrint[0] ? logsForPrint : logs;
      pdfHelper<ILogs>(arrOfLogs, logsColumns, t('Logs.managament.report'))
    } catch (e) {
      messageApi?.error(t('Logs.managament.reportError'));
    } finally {
      setDownloadProcess(downloadProcess.filter(el => el === 'print'));
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'download',
      className: classes.liItem,
      label: (
        <Button
          loading={downloadProcess.includes('download')}
          onClick={downloadReport}
          icon={<DownloadOutlined />}
          className={classes.liButton}
          disabled={Boolean(downloadProcess[0])}
        >
          {t('Logs.managament.download')}
        </Button>
      ),
    },
    {
      key: 'print',
      className: classes.liItem,
      label: (
        <Button
          loading={downloadProcess.includes('print')}
          onClick={printReport}
          icon={<PrinterOutlined />}
          className={classes.liButton}
          disabled={Boolean(downloadProcess[0])}
        >
          {t('Logs.managament.print')}
        </Button>
      ),
    },
  ];

  return (
    <div className={classes.managementArea}>
      { (isLoadingRoom || userAndRoomStatus !== '')
        ? <ManagementLoader/> 
        : <div className={classes.managementWrap}>
            <div className={classes.managementTitle}>
              {t('Logs.managament.title')}
            </div>
            <div className={classes.optionsSection}>
              <RangePicker
                showTime={{
                  format: 'HH:mm',
                  defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59:59', 'HH:mm:ss')]
                }}
                format={['DD.MM.YYYY HH:mm']}
                className={classes.rangePicker}
                value={choosenDates && [choosenDates[0], choosenDates[1]]}
                presets={rangePresets}
                onChange={onRangeChange}
              />
              <Button
                icon={<ReloadOutlined  />}
                className={classes.greyIcon}
                onClick={refreshLogs}
              />
              <Dropdown
                // trigger={['click']}
                open={isOpenDropdown || Boolean(downloadProcess[0])}
                onOpenChange={(open) => setIsOpenDropdown(open)}
                className={classes.dropdown}
                menu={{ items }}
                placement='bottomLeft'
              >
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            </div>
        </div>
      }
    </div>
  )
}
